import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ContactsService } from '../../shared/services/contacts.service';
import {
  getEditedFilterItemsSelector,
  getEditedFiltersSelector,
  getTempEditedFiltersSelector,
  getTempPageSelector,
  getTempTagsSelector
} from '../../contacts.reducer';
import { Campaign, FiltersEditedItems, FilterTagGroup, Page } from '../../shared/models/Contact';
import { Store } from '@ngrx/store';
import { IState } from '../../../shared/models/state';
import {
  SetDateRangeAction,
  ClearTagGroupTooltip,
  SetFilterItemOpened,
  GetFilterMyNumbersAction,
  GetFilterUsersAction,
  GetFilterEventTypesAction,
  GetFilterCallLengthsAction,
  SetCallFinishTypeFilter,
  RemoveCallFinishTypeFilter,
  TakeCallFinishTypeSnapshotAction,
  GetFilterByTagsAction,
  UpdateTempEditedFilterList
} from '../../contacts.actions';
import * as moment from 'moment';

import { trigger, state, style, animate, transition } from '@angular/animations';
import { FilterService } from '../../shared/services/filter.service';
import { DatePickerService } from '../../../shared/services/date.picker.service';

@Component({
  selector: 'cc-filters-search',
  animations: [
    trigger('openClose', [
      state(
        'open',
        style({
          opacity: 1,
          top: 0,
          right: 0
        })
      ),
      state(
        'closed',
        style({
          opacity: 1,
          top: 0,
          right: '-100%'
        })
      ),
      transition('open => closed', [animate('2s ease')]),
      transition('closed => open', [animate('0.5s')])
    ])
  ],
  templateUrl: './filters-search.component.html',
  styleUrls: ['./filters-search.component.scss']
})
export class FiltersSearchComponent implements OnInit {
  public filtersOpened: boolean;
  public searchFilterOpened: boolean;
  public lastDayPicker: boolean;
  public datePickerStartDate: moment.Moment | string;
  public datePickerEndDate: moment.Moment | string;
  public utcOffset = moment().utcOffset();
  public maxDate = moment().add(this.utcOffset / 60, 'hours').toISOString();
  public allChecked: boolean;
  public indeterminate: boolean;
  public tempTags: FilterTagGroup;
  public tagsWasChosen: boolean;
  public dataRangeHasChanged: boolean;
  public dateTagView: string;
  public basicFiltersDone: boolean;
  public tempStartDate: string | moment.Moment;
  public tempEndDate: string | moment.Moment;
  public isFilteringReady: boolean;
  public editedFiltersList: string[];
  public tempEditedFiltersList: string[];
  public isVoiceMailRecordingsFiltered: boolean;
  public voiceMailRecordingsHasChanged: boolean;
  @ViewChild('searchIconCalendar') searchIconCalendar: ElementRef;

  constructor(
    public contactsService: ContactsService,
    public datePickerService: DatePickerService,
    private store: Store<IState>,
    public filterService: FilterService
  ) {}

  ngOnInit() {
    this.contactsService.toggleOpeningFilters.subscribe((opener: boolean) => {
      this.filtersOpened = opener;
    });

    this.contactsService.toggleOpeningSearchFilters.subscribe((opener: boolean) => {
      this.searchFilterOpened = opener;
    });

    this.contactsService.onRangeChanged.subscribe((event: any) => {
      if (event) {
        this.changeDateRange({ dateFrom: event.dateFrom, dateTo: event.dateTo });
      }
    });

    this.store.select(getTempPageSelector).subscribe((page: Page) => {
      if (page && page.filter) {
        this.tempStartDate = page.filter.dateFrom;
        this.tempEndDate = page.filter.dateTo;
        this.dateTagView = this.getRangeTagName({ dateFrom: this.tempStartDate, dateTo: this.tempEndDate });
        this.datePickerStartDate = moment(this.tempStartDate).toISOString();
        this.datePickerEndDate = moment(this.tempEndDate).toISOString();
        this.isVoiceMailRecordingsFiltered =
          page.filter.callFinishType && page.filter.callFinishType === 'INCOMING_CALL_VOICEMAIL';
        if (this.isVoiceMailRecordingsFiltered) {
          const extendedTempEditedFiltersList = this.filterService.getEditedFiltersListUpdate(
            'callFinishType',
            this.tempEditedFiltersList ? this.tempEditedFiltersList : []
          );
          this.store.dispatch(new UpdateTempEditedFilterList(extendedTempEditedFiltersList));
        }
      }
    });

    this.store.select(getTempTagsSelector).subscribe((tempTags: FilterTagGroup) => {
      this.tempTags = tempTags;

      if (this.tempTags && Object.keys(this.tempTags).length) {
        const existingTags = Object.keys(this.tempTags).map(key => {
          return this.tempTags[key];
        });

        this.tagsWasChosen = !!existingTags.length;
      }
    });

    this.store.select(getEditedFilterItemsSelector).subscribe((filterItems: FiltersEditedItems) => {
      if (filterItems) {
        this.basicFiltersDone =
          (filterItems['users'] && !!filterItems['users'].length) ||
          (filterItems['clientPhoneNumbers'] && !!filterItems['clientPhoneNumbers'].length) ||
          (filterItems['callerIds'] && !!filterItems['callerIds'].length) ||
          (filterItems['eventTypes'] && !!filterItems['eventTypes'].length) ||
          (filterItems['tags'] && !!filterItems['tags'].length) ||
          (filterItems['callLengths'] && !!filterItems['callLengths'].length);
      }
    });

    this.contactsService.onOpenFilterToEdit.subscribe((filterName: string) => {
      this.editFilter(filterName);
    });

    this.store.select(getEditedFiltersSelector).subscribe((editedFiltersList: string[]) => {
      this.editedFiltersList = editedFiltersList;
      this.isFilteringReady = !!this.editedFiltersList && !!this.editedFiltersList.length;
    });

    this.store.select(getTempEditedFiltersSelector).subscribe((tempEditedFiltersList: string[]) => {
      this.tempEditedFiltersList = tempEditedFiltersList;
    });

    this.contactsService.onFilterEditDone.subscribe(() => {
      this.isFilteringReady = !!this.editedFiltersList && !!this.editedFiltersList.length;
    });

    this.contactsService.onSnapshotCallFinishType.subscribe(() => {
      this.takeVoicemailRecordingsSnapshot();
    });

    this.indeterminate = false;
  }

  closeFilters() {
    this.contactsService.closeAllFilters();
  }

  changeLastDayLog(event) {
    this.lastDayPicker = event;
    let filters;

    if (event) {
      filters = {
        dateFrom: moment()
          .subtract(24, 'hour')
          .toISOString(),
        dateTo: moment().toISOString()
      };
    } else {
      filters = {
        dateFrom: moment(this.datePickerStartDate).toISOString(),
        dateTo: moment(this.datePickerEndDate).toISOString()
      };
    }
    this.store.dispatch(new SetDateRangeAction(filters));
    this.dateTagView = this.getRangeTagName(filters);
    this.dataRangeHasChanged = true;
  }

  changeDateRange(event) {
    this.dataRangeHasChanged = true;
    const newFilters = {
      dateFrom: moment(event.dateFrom).toISOString(),
      dateTo: moment(event.dateTo).toISOString()
    };
    this.store.dispatch(new SetDateRangeAction(newFilters));
    this.dateTagView = this.getRangeTagName(newFilters);
  }

  filterVoiceMailRecordings(event) {
    if (event) {
      const newTempEditedFiltersList = this.filterService.getEditedFiltersListUpdate(
        'callFinishType',
        this.tempEditedFiltersList
      );
      this.store.dispatch(new SetCallFinishTypeFilter('INCOMING_CALL_VOICEMAIL'));
      this.store.dispatch(new UpdateTempEditedFilterList(newTempEditedFiltersList));
    } else {
      const newTempEditedFiltersList = this.tempEditedFiltersList.filter(item => item !== 'callFinishType');
      this.store.dispatch(new UpdateTempEditedFilterList(newTempEditedFiltersList));
      this.store.dispatch(new RemoveCallFinishTypeFilter('callFinishType'));
    }
    this.voiceMailRecordingsHasChanged = true;
  }

  getRangeTagName(rangeFilter: { dateFrom: moment.Moment | string; dateTo: moment.Moment | string }) {
    return `${moment(rangeFilter.dateFrom).format('MM.DD')} - ${moment(rangeFilter.dateTo).format('MM.DD')}`;
  }

  setNewFilters() {
    this.store.dispatch(new ClearTagGroupTooltip());
    this.contactsService.useNewFilters();
    this.contactsService.hideFilters();
    this.searchFilterOpened = false;
    this.contactsService.applyChangesNotifier(false);
    this.contactsService.clearMassiveActions(false, []);
  }

  editFilter(filterName: string) {
    this.store.dispatch(new SetFilterItemOpened(filterName));

    switch (filterName) {
      case 'callerIds':
        this.store.dispatch(new GetFilterMyNumbersAction(filterName));
        this.contactsService.showUniversalFilters();
        return;
      case 'users':
        this.store.dispatch(new GetFilterUsersAction(filterName));
        this.contactsService.showUniversalFilters();
        return;
      case 'eventTypes':
        this.store.dispatch(new GetFilterEventTypesAction(filterName));
        this.contactsService.showUniversalFilters();
        return;
      case 'tags':
        this.store.dispatch(new GetFilterByTagsAction(filterName));
        this.contactsService.showUniversalFilters();
        return;
      case 'callLengths':
        this.store.dispatch(new GetFilterCallLengthsAction(filterName));
        this.contactsService.showCallLengthsFilters();
        return;
      case 'clientPhoneNumbers':
        this.contactsService.showPhoneNumbersFilters();
        return;
    }
  }

  isFilterEdited(filter: string) {
    return this.tempEditedFiltersList && this.tempEditedFiltersList.includes(filter);
  }

  exitFilters($event) {
    if ($event.target.className === 'nav-text' && this.filtersOpened && this.searchFilterOpened) {
      this.contactsService.closeAllFilters();
    }
  }

  takeVoicemailRecordingsSnapshot() {
    this.store.dispatch(new TakeCallFinishTypeSnapshotAction());
  }

  openPicker() {
    this.datePickerService.toggleDatePicker({
      open: 'left',
      show: true,
      positionX: this.searchIconCalendar.nativeElement.offsetLeft,
      positionY: this.searchIconCalendar.nativeElement.offsetTop + 50
    });
  }
}
